export interface FrontendConfig {
    readonly entra: {
        readonly clientId: string;
        readonly tenantId: string;
        readonly exchangeScope: string;
    };
    readonly urls: {
        readonly apiBaseUrl: string;
        readonly appPath: string;
        readonly basePath: string;
        readonly helpUrl: string;
    };
    readonly locale: {
        readonly initialLocale: string;
        readonly datePattern: string;
        readonly timePattern: string;
        readonly timezone: string;
    };
}

export type FrontendConfigSource = 'build' | 'runtime';

type BuildEnvironment = Record<string, string | boolean | undefined>;

declare global {
    interface Window {
        __VPLANT_RUNTIME_CONFIG__?: unknown;
    }
}

function requireObject(value: unknown, fieldName: string): Record<string, unknown> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`${fieldName} must be an object.`);
    }

    return value as Record<string, unknown>;
}

function requireString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(`${fieldName} must be a non-empty string.`);
    }

    return value.trim();
}

function optionalString(value: unknown, fieldName: string): string | undefined {
    if (value === undefined) return undefined;
    if (typeof value !== 'string') throw new Error(`${fieldName} must be a string.`);
    return value;
}

function normalizeBasePath(value: string | undefined): string {
    const path = value?.trim();
    if (!path || path === '/') return '/';
    return `/${path.replace(/^\/+|\/+$/g, '')}/`;
}

function deepFreeze<T>(value: T): Readonly<T> {
    if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        Object.values(value).forEach(deepFreeze);
    }

    return value;
}

interface ConfigValues {
    clientId: string;
    tenantId: string;
    exchangeScope?: string;
    apiBaseUrl?: string;
    appPath?: string;
    basePath?: string;
    helpUrl?: string;
    initialLocale?: string;
    datePattern?: string;
    timePattern?: string;
    timezone?: string;
}

function normalizeConfig(values: ConfigValues, browserLanguage: string | undefined): FrontendConfig {
    const clientId = values.clientId.trim();
    const tenantId = values.tenantId.trim();

    return deepFreeze({
        entra: {
            clientId,
            tenantId,
            exchangeScope: values.exchangeScope?.trim() || `api://${clientId}/AnyApi`,
        },
        urls: {
            apiBaseUrl: values.apiBaseUrl?.trim() || '/api',
            appPath: values.appPath?.trim() || '',
            basePath: normalizeBasePath(values.basePath),
            helpUrl: values.helpUrl?.trim() || '',
        },
        locale: {
            initialLocale: values.initialLocale?.trim() || browserLanguage || 'en-US',
            datePattern: values.datePattern?.trim() || 'MM/dd/yyyy',
            timePattern: values.timePattern?.trim() || 'hh:mm a',
            timezone: values.timezone?.trim() || '',
        },
    });
}

export function resolveRuntimeFrontendConfig(rawValue: unknown, browserLanguage?: string): FrontendConfig {
    const raw = requireObject(rawValue, 'runtime configuration');
    if (raw.schemaVersion !== 1) {
        throw new Error('schemaVersion must be 1.');
    }

    const entra = requireObject(raw.entra, 'entra');
    const urls = requireObject(raw.urls, 'urls');
    const locale = requireObject(raw.locale, 'locale');

    return normalizeConfig({
        clientId: requireString(entra.clientId, 'entra.clientId'),
        tenantId: requireString(entra.tenantId, 'entra.tenantId'),
        exchangeScope: optionalString(entra.exchangeScope, 'entra.exchangeScope'),
        apiBaseUrl: optionalString(urls.apiBaseUrl, 'urls.apiBaseUrl'),
        appPath: optionalString(urls.appPath, 'urls.appPath'),
        basePath: optionalString(urls.basePath, 'urls.basePath'),
        helpUrl: optionalString(urls.helpUrl, 'urls.helpUrl'),
        initialLocale: optionalString(locale.initialLocale, 'locale.initialLocale'),
        datePattern: optionalString(locale.datePattern, 'locale.datePattern'),
        timePattern: optionalString(locale.timePattern, 'locale.timePattern'),
        timezone: optionalString(locale.timezone, 'locale.timezone'),
    }, browserLanguage);
}

export function resolveBuildFrontendConfig(environment: BuildEnvironment, browserLanguage?: string): FrontendConfig {
    return normalizeConfig({
        clientId: String(environment.VITE_ENTRA_CLIENT_ID || ''),
        tenantId: String(environment.VITE_ENTRA_TENANT_ID || ''),
        exchangeScope: String(environment.VITE_ENTRA_EXCHANGE_SCOPE || ''),
        apiBaseUrl: String(environment.VITE_API_BASE_URL || ''),
        appPath: String(environment.VITE_APP_PATH || ''),
        basePath: String(environment.VITE_BASE_PATH || ''),
        helpUrl: String(environment.VITE_HELP_URL || ''),
        initialLocale: String(environment.VITE_LOCALE || ''),
        datePattern: String(environment.VITE_DATE_PATTERN || ''),
        timePattern: String(environment.VITE_TIME_PATTERN || ''),
        timezone: String(environment.VITE_TIMEZONE || ''),
    }, browserLanguage);
}

export function detectFrontendConfigSource(doc: Pick<Document, 'querySelector'>): FrontendConfigSource {
    return doc.querySelector('script[data-vplant-config-source="runtime"]') ? 'runtime' : 'build';
}

// This screen is the one failure path that cannot use InovxLoader: the runtime
// configuration that supplies the active locale is exactly what failed to load,
// and i18next has not been initialized when this runs. The strings are therefore
// kept inline, resolved from the browser locale only, and limited to this screen.
const RUNTIME_CONFIG_ERROR_TEXT = {
    'en-US': {
        documentTitle: 'V-Plant configuration error',
        heading: 'V-Plant could not start',
        message: 'The application runtime configuration is missing or invalid. Contact the system administrator.',
        unknownDetail: 'Unknown runtime configuration error.',
    },
    'ja-JP': {
        documentTitle: 'V-Plant 構成エラー',
        heading: 'V-Plant を起動できませんでした',
        message: 'アプリケーションの実行時構成が見つからないか、無効です。システム管理者にお問い合わせください。',
        unknownDetail: '不明な実行時構成エラーです。',
    },
} as const;

function getRuntimeConfigErrorText(browserLanguage?: string) {
    const language = (browserLanguage ?? navigator.language ?? '').toLowerCase();
    return language === 'ja' || language.startsWith('ja-')
        ? RUNTIME_CONFIG_ERROR_TEXT['ja-JP']
        : RUNTIME_CONFIG_ERROR_TEXT['en-US'];
}

function renderRuntimeConfigurationError(error: unknown): void {
    const text = getRuntimeConfigErrorText();
    document.title = text.documentTitle;
    const container = document.createElement('main');
    const heading = document.createElement('h1');
    const message = document.createElement('p');
    const detail = document.createElement('p');
    heading.textContent = text.heading;
    message.textContent = text.message;
    detail.textContent = error instanceof Error ? error.message : text.unknownDetail;
    container.setAttribute('role', 'alert');
    container.append(heading, message, detail);
    document.body.replaceChildren(container);
}

let resolvedConfig: FrontendConfig | undefined;

export function getFrontendConfig(): FrontendConfig {
    if (resolvedConfig) return resolvedConfig;

    const source = detectFrontendConfigSource(document);
    try {
        resolvedConfig = source === 'runtime'
            ? resolveRuntimeFrontendConfig(window.__VPLANT_RUNTIME_CONFIG__, navigator.language)
            : resolveBuildFrontendConfig(import.meta.env, navigator.language);
        return resolvedConfig;
    } catch (error) {
        if (source === 'runtime') renderRuntimeConfigurationError(error);
        throw error;
    }
}
